/**
 * ThemeRegistry
 * ------------------------
 * Holds only METADATA + a lazy loader function per theme — never the
 * theme's actual code or token values. This is what makes it possible
 * for Core to "know about" a theme's existence without ever statically
 * importing it.
 *
 * Registration can happen from anywhere (a theme package's own bootstrap
 * file, a build-time manifest scan, or a future remote theme-store
 * response) — the registry itself has zero knowledge of where entries
 * come from, which is what enables future dynamic discovery.
 */
export class ThemeRegistry {
    constructor() {
        this.entries = new Map();
    }
    /** Register a theme. Throws if the id is already taken to prevent silent overwrites. */
    register(entry) {
        if (this.entries.has(entry.id)) {
            throw new Error(`ThemeRegistry: a theme with id "${entry.id}" is already registered.`);
        }
        this.entries.set(entry.id, entry);
    }
    /** Register multiple themes at once (e.g. during app bootstrap). */
    registerMany(entries) {
        entries.forEach((entry) => this.register(entry));
    }
    /** Unregister a theme (e.g. a premium theme revoked at runtime). */
    unregister(id) {
        this.entries.delete(id);
    }
    /** Whether a theme id is known to the registry. */
    has(id) {
        return this.entries.has(id);
    }
    /** Get a single registry entry (metadata + loader), or undefined. */
    get(id) {
        return this.entries.get(id);
    }
    /** List all registered theme entries (metadata-only, no code loaded). */
    list() {
        return Array.from(this.entries.values());
    }
    /** Number of registered themes. */
    count() {
        return this.entries.size;
    }
    /**
     * Reserved hook for future dynamic discovery (e.g. fetching a remote
     * theme catalog and registering entries on the fly). Intentionally
     * left as an extension point rather than implemented now.
     */
    async discover(_source) {
        // Future phase: fetch remote/local theme catalogs and call registerMany().
        return;
    }
}
/**
 * A shared, app-wide registry instance. Core code should depend on this
 * singleton rather than constructing its own ThemeRegistry, so all
 * theme registrations (wherever they happen) land in one place.
 */
export const themeRegistry = new ThemeRegistry();
